import type { CSSProperties } from 'react';
import { Calendar, Hourglass } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { isOverdue, parseDateOnly, startOfDay } from '../../lib/assigned-queue';
import { APP_LOCALE, formatDateOnly, formatFriendlyDateOnly, lateText } from '../../lib/dates';
import { PRIORITY_LABELS } from '../../lib/service-requests';
import { memberDisplayName, memberGivenName, type LabelDto, type MemberDto } from '../../lib/work-types';
import { cn } from '../../lib/utils';
import { UserAvatar } from '../UserAvatar';

export type CardMetaItem = {
  label_ids?: string[];
  assignee_ids?: string[];
  start_date?: string | null;
  due_date?: string | null;
};

function isHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

function idList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
}

/** Resolves an item's label ids against the project's labels, dropping unknown ids. */
export function selectedLabels(labelIds: unknown, labels: LabelDto[]): LabelDto[] {
  return idList(labelIds)
    .map((id) => labels.find((label) => label.id === id))
    .filter((label): label is LabelDto => Boolean(label));
}

/** Resolves an item's assignee ids against the project's members, dropping unknown ids. */
export function selectedMembers(assigneeIds: unknown, members: MemberDto[]): MemberDto[] {
  return idList(assigneeIds)
    .map((id) => members.find((member) => member.user_id === id))
    .filter((member): member is MemberDto => Boolean(member));
}

/** A locale-formatted range with year, or null when neither date is set. */
export function formatDateRange(
  start: string | null | undefined,
  due: string | null | undefined,
): { text: string; overdue: boolean } | null {
  const startValue = typeof start === 'string' && start.length > 0 ? start : null;
  const dueValue = typeof due === 'string' && due.length > 0 ? due : null;
  const startLabel = startValue ? formatDateOnly(startValue) : null;
  const dueLabel = dueValue ? formatDateOnly(dueValue) : null;
  const text =
    startLabel && dueLabel && startLabel !== dueLabel
      ? `${startLabel} - ${dueLabel}`
      : dueLabel ?? startLabel;
  if (!text) return null;
  return { text, overdue: dueValue ? isOverdue(dueValue, startOfDay(new Date())) : false };
}

function formatCardDay(value: string): string | null {
  const parsed = parseDateOnly(value);
  if (!parsed) return null;
  return new Intl.DateTimeFormat(APP_LOCALE, { day: 'numeric', month: 'short' }).format(parsed);
}

/** Short range like `5 Sept – 18 Sept`, or null when neither date is set. */
export function formatCardDateRange(
  start: string | null | undefined,
  due: string | null | undefined,
): { text: string; overdue: boolean } | null {
  const startValue = typeof start === 'string' && start.length > 0 ? start : null;
  const dueValue = typeof due === 'string' && due.length > 0 ? due : null;
  const startLabel = startValue ? formatCardDay(startValue) : null;
  const dueLabel = dueValue ? formatCardDay(dueValue) : null;
  const text =
    startLabel && dueLabel && startLabel !== dueLabel
      ? `${startLabel} – ${dueLabel}`
      : dueLabel ?? startLabel;
  if (!text) return null;
  return { text, overdue: dueValue ? isOverdue(dueValue, startOfDay(new Date())) : false };
}

/**
 * Fills a chip with the label's own colour. Labels are user-defined, so the text
 * colour is picked from the background's luminance to stay legible on both a
 * pale yellow and a dark navy.
 */
export function labelChipStyle(color: string): CSSProperties | undefined {
  if (!isHexColor(color)) return undefined;
  const red = parseInt(color.slice(1, 3), 16);
  const green = parseInt(color.slice(3, 5), 16);
  const blue = parseInt(color.slice(5, 7), 16);
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return { backgroundColor: color, color: luminance > 0.6 ? '#1f2937' : '#ffffff' };
}

export function MemberAvatar({
  member,
  size = 'sm',
  className,
}: {
  member: MemberDto;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const name = memberDisplayName(member);
  return (
    <UserAvatar
      name={name}
      src={member.avatar_url}
      size={size === 'md' ? 32 : 24}
      className={className}
    />
  );
}

const PRIORITIES = ['urgent', 'high', 'medium', 'low', 'none'] as const;
type Priority = (typeof PRIORITIES)[number];

function isPriority(value: string): value is Priority {
  return (PRIORITIES as readonly string[]).includes(value);
}

/** Pill with a written label so priority is never color-only. Service requests use the PRD names (Critical/High/Normal/Low). */
export function CardPriority({ priority, type }: { priority: string; type?: string | null }) {
  if (!isPriority(priority) || priority === 'none') return null;
  const serviceRequest = type === 'service_request';
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium leading-4',
        !serviceRequest && 'capitalize',
        priority === 'urgent' && 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
        priority === 'high' && 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
        priority === 'medium' && 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
        priority === 'low' && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
      )}
    >
      {serviceRequest ? PRIORITY_LABELS[priority] : priority}
    </span>
  );
}

export function WorkCardLabels({
  item,
  labels,
}: {
  item: CardMetaItem;
  labels: LabelDto[];
}) {
  const selected = selectedLabels(item.label_ids, labels);
  if (selected.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {selected.map((label) => (
        <span
          key={label.id}
          title={label.name}
          className="inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[11px] leading-4 text-muted-foreground"
        >
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={isHexColor(label.color) ? { backgroundColor: label.color } : undefined}
            aria-hidden
          />
          {label.name}
        </span>
      ))}
    </div>
  );
}

/** "Waiting on legal" stays as written; anything else reads "Waiting: <reason>". */
export function waitingText(reason: string): string {
  return /^(waiting|blocked)\b/i.test(reason.trim()) ? reason.trim() : `Blocked: ${reason.trim()}`;
}

export function BlockedBadge({ reason, className }: { reason?: string | null; className?: string }) {
  return (
    <AnimatePresence initial={false}>
      {reason ? (
        <m.span
          key="blocked"
          title={`Blocked: ${reason}`}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.22, ease: [0.16, 1, 0.3, 1] } }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            'inline-flex max-w-full origin-left items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900',
            className,
          )}
        >
          <Hourglass className="h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate">{waitingText(reason)}</span>
        </m.span>
      ) : null}
    </AnimatePresence>
  );
}

/** "Fri 23 Oct" for the due date (or "Starts …" when only a start is set), plus lateness in words. */
export function cardWhen(
  item: CardMetaItem,
  done: boolean,
  today: Date = new Date(),
): { text: string; late: string | null } | null {
  const due = typeof item.due_date === 'string' && item.due_date.length > 0 ? item.due_date : null;
  if (due) {
    const text = formatFriendlyDateOnly(due, today);
    if (!text) return null;
    return { text: done ? text : `Due ${text}`, late: done ? null : lateText(due, today) };
  }
  const start = typeof item.start_date === 'string' && item.start_date.length > 0 ? item.start_date : null;
  const startText = start ? formatFriendlyDateOnly(start, today) : null;
  return startText ? { text: `Starts ${startText}`, late: null } : null;
}

export function WorkCardBadges({
  item,
  members,
  done = false,
}: {
  item: CardMetaItem;
  members: MemberDto[];
  /** Done work never reads as late. */
  done?: boolean;
}) {
  const when = cardWhen(item, done);
  const assignees = selectedMembers(item.assignee_ids, members);

  return (
    <div className="flex items-start justify-between gap-2">
      {when ? (
        <span
          className={cn(
            'inline-flex min-w-0 items-start gap-1.5 text-[12px] leading-4 text-muted-foreground',
            when.late && 'text-red-700 dark:text-red-300',
          )}
        >
          <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">
            <span className="block truncate">{when.text}</span>
            {when.late ? <span className="block font-medium">{when.late}</span> : null}
          </span>
        </span>
      ) : (
        <span className="text-[12px] leading-4 text-muted-foreground/70">No date</span>
      )}
      {assignees.length > 0 ? (
        <span
          className="inline-flex min-w-0 shrink-0 items-center gap-1.5"
          title={assignees.map((member) => memberDisplayName(member)).join(', ')}
        >
          <span className="flex -space-x-1.5">
            {assignees.slice(0, 2).map((member) => (
              <MemberAvatar key={member.user_id} member={member} className="border-2 border-card" />
            ))}
          </span>
          <span className="max-w-[6rem] truncate text-[12px] leading-4 text-muted-foreground">
            {memberGivenName(assignees[0]!)}
            {assignees.length > 1 ? ` +${assignees.length - 1}` : ''}
          </span>
        </span>
      ) : (
        <span className="text-[12px] leading-4 text-muted-foreground/70">No owner</span>
      )}
    </div>
  );
}
